import { cloneObject } from "./Cloneable";
import { encodeObject } from "./Encodeable";
import { patchObject } from "./Patchable";
/**
 * Uses the meta data of AutoEncoder to check if something is a patch or a put
 */
export class PatchOrPutDecoder {
    constructor(put, patch) {
        this.putDecoder = put;
        this.patchDecoder = patch;
    }
    decode(data) {
        var _a;
        const isPatch = data.optionalField("_isPatch");
        if ((_a = isPatch === null || isPatch === void 0 ? void 0 : isPatch.boolean) !== null && _a !== void 0 ? _a : false) {
            return this.patchDecoder.decode(data);
        }
        return this.putDecoder.decode(data);
    }
}
export class Field {
    getOptionalClone() {
        const field = new Field();
        field.optional = true;
        field.nullable = this.nullable;
        field.decoder = this.decoder;
        field.version = this.version;
        field.property = this.property;
        field.field = this.field;
        if (this.upgrade) {
            const upg = this.upgrade;
            field.upgrade = (oldValue) => {
                if (oldValue !== undefined) {
                    // Value is set, we need an upgrade 
                    return upg(oldValue);
                }
                else {
                    // No value is set, we don't need an upgrade
                    return undefined;
                }
            };
        }
        if (this.downgrade) {
            const dwn = this.downgrade;
            field.downgrade = (newValue) => {
                if (newValue !== undefined) {
                    // Value is set, we need an upgrade 
                    return dwn(newValue);
                }
                else {
                    // No value is set, we don't need an upgrade
                    return undefined;
                }
            };
        }
        if (this.upgradePatch) {
            field.upgrade = this.upgradePatch;
        }
        if (this.downgradePatch) {
            field.downgrade = this.downgradePatch;
        }
        field.upgradePatch = this.upgradePatch;
        field.downgradePatch = this.downgradePatch;
        field.patchDefaultValue = this.patchDefaultValue;
        field.defaultValue = undefined; // do not copy default values. Patches never have default values, unless for patchable arrays
        const aDecoder = this.decoder;
        // Do we have a custom patch decoder? (this can be configured in the decoder)
        if (aDecoder.patchType) {
            field.upgrade = this.upgradePatch;
            field.downgrade = this.downgradePatch;
            const patchDecoder = aDecoder.patchType();
            field.decoder = new PatchOrPutDecoder(aDecoder, patchDecoder);
        }
        if (aDecoder.patchDefaultValue) {
            // e.g. for patchable arrays we always set a default value
            field.defaultValue = () => {
                return aDecoder.patchDefaultValue();
            };
        }
        if (this.patchDefaultValue) {
            field.defaultValue = this.patchDefaultValue;
        }
        return field;
    }
}
/**
 * Create patchable auto encoder.
 * We are not able to add types here, gets too complex. But we'll add a convenience method with typings
 */
/*export function createPatchableAutoEncoder(constructor: typeof AutoEncoder): typeof AutoEncoder {
    return constructor as any;
}*/
/*
class Dog extends AutoEncoder {
    id: string;
    name: string;
}

const DogPatch = createPatchableAutoEncoder(Dog);

const p = DogPatch.create({id: "test"})

*/
export class AutoEncoder {
    /// Create a patch for this instance (of reuse if already created)
    static patchType() {
        if (this.cachedPatchType) {
            return this.cachedPatchType;
        }
        // create a new class
        class CreatedPatch extends AutoEncoder {
        }
        CreatedPatch.fields = [];
        // A patchtype of a patchtype is always the same
        // -> avoids infinite loop and allows recursive encoding
        CreatedPatch.cachedPatchType = CreatedPatch;
        this.cachedPatchType = CreatedPatch;
        // Move over all fields
        for (const field of this.fields) {
            CreatedPatch.fields.push(field.getOptionalClone());
        }
        // move over getIdentifier if available
        if (this.prototype.getIdentifier && this.prototype.getIdentifier instanceof Function) {
            CreatedPatch.prototype.getIdentifier = this.prototype.getIdentifier;
        }
        CreatedPatch.isPatch = true;
        return CreatedPatch;
    }
    constructor() {
        if (!this.static.fields) {
            this.static.fields = [];
        }
        for (const field of this.static.latestFields) {
            if (field.defaultValue) {
                this[field.property] = field.defaultValue();
            }
        }
    }
    isPatch() {
        return this.static.isPatch;
    }
    isPut() {
        return !this.static.isPatch;
    }
    static patch(object) {
        return this.patchType().create(object);
    }
    patchOrPut(patch) {
        if (patch.static.isPatch) {
            this.set(this.patch(patch));
            return;
        }
        this.set(patch);
    }
    /**
     * Make a deep clone of this object
     */
    clone() {
        const instance = new this.static();
        for (const field of this.static.latestFields) {
            const prop = field.property;
            instance[prop] = cloneObject(this[prop]);
        }
        return instance;
    }
    patch(patch) {
        const instance = new this.static();
        for (const field of this.static.latestFields) {
            const prop = field.property;
            const patched = patchObject(this[prop], patch[prop]);
            if (patched !== undefined) {
                instance[prop] = patched;
            }
        }
        return instance;
    }
    static sortFields() {
        function compare(a, b) {
            if (a.version < b.version) {
                return -1;
            }
            if (a.version > b.version) {
                return 1;
            }
            // a must be equal to b
            return 0;
        }
        this.fields.sort(compare);
    }
    static get latestFields() {
        const latestFields = {};
        for (let i = this.fields.length - 1; i >= 0; i--) {
            const field = this.fields[i];
            if (!latestFields[field.property]) {
                latestFields[field.property] = field;
            }
        }
        return Object.values(latestFields);
    }
    static doesPropertyExist(property) {
        for (const field of this.fields) {
            if (field.property === property) {
                return true;
            }
        }
        return false;
    }
    /**
     * Create a new one by providing the properties of the object
     */
    static create(object) {
        const model = new this();
        for (const key in object) {
            // eslint-disable-next-line no-prototype-builtins
            if (object.hasOwnProperty(key) && object[key] !== undefined && typeof object[key] !== "function") {
                // Also check this is an allowed field, else skip in favor of allowing downcasts without errors
                if (this.doesPropertyExist(key)) {
                    model[key] = object[key];
                }
            }
        }
        for (const field of this.latestFields) {
            if (!field.optional) {
                if (model[field.property] === undefined) {
                    throw new Error("Expected required property " + field.property + " when creating " + this.name);
                }
            }
            if (!field.nullable) {
                if (model[field.property] === null) {
                    throw new Error("Expected non null property " + field.property + " when creating " + this.name);
                }
            }
        }
        return model;
    }
    /**
     * Create a new one by providing the properties of the object
     */
    set(object) {
        for (const key in object) {
            if (object.hasOwnProperty(key) && typeof object[key] !== "function") {
                if (this.static.doesPropertyExist(key)) {
                    this[key] = object[key];
                }
            }
        }
    }
    get static() {
        return this.constructor;
    }
    encode(context) {
        const object = {};
        const source = this.static.downgrade(context.version, this);
        const appliedProperties = {};
        for (let i = this.static.fields.length - 1; i >= 0; i--) {
            const field = this.static.fields[i];
            if (field.version <= context.version && !appliedProperties[field.property]) {
                if (source[field.property] === undefined) {
                    if (!field.optional) {
                        throw new Error("Value for property " + field.property + " is not set, but is required!");
                    }
                    continue;
                }
                object[field.field] = encodeObject(source[field.property], context);
                appliedProperties[field.property] = true;
            }
        }
        // Add meta data
        if (this.static.isPatch) {
            object["_isPatch"] = this.static.isPatch;
        }
        return object;
    }
    static decode(data) {
        var _a, _b, _c, _d, _e, _f;
        const model = new this();
        const appliedProperties = {};
        // Loop from newest version to older version
        for (let i = this.fields.length - 1; i >= 0; i--) {
            const field = this.fields[i];
            if (field.version <= data.context.version && !appliedProperties[field.property]) {
                if (field.optional) {
                    if (field.nullable) {
                        model[field.property] = (_a = data.undefinedField(field.field)) === null || _a === void 0 ? void 0 : _a.nullable(field.decoder);
                    }
                    else {
                        model[field.property] = (_d = (_c = (_b = data.optionalField(field.field)) === null || _b === void 0 ? void 0 : _b.decode(field.decoder)) !== null && _c !== void 0 ? _c : model[field.property]) !== null && _d !== void 0 ? _d : undefined;
                    }
                }
                else {
                    if (field.nullable) {
                        model[field.property] = (_e = data.field(field.field)) === null || _e === void 0 ? void 0 : _e.nullable(field.decoder);
                    }
                    else {
                        model[field.property] = (_f = data.field(field.field)) === null || _f === void 0 ? void 0 : _f.decode(field.decoder);
                    }
                }
                appliedProperties[field.property] = true;
            }
        }
        // We now have model with values equal to version data.context.version
        // Run upgrade / downgrade migrations to convert changes in fields
        this.upgrade(data.context.version, model);
        return model;
    }
    /**
     * Upgrade property values coming from an older version
     * @param from
     * @param object
     */
    static upgrade(from, object) {
        for (const field of this.fields) {
            if (field.version > from) {
                if (field.upgrade) {
                    object[field.property] = field.upgrade.call(object, object[field.property]);
                }
            }
        }
    }
    /**
     * Downgrade property values to a new object
     */
    static downgrade(to, object) {
        const older = Object.assign({}, object);
        for (let i = this.fields.length - 1; i >= 0; i--) {
            const field = this.fields[i];
            if (field.version > to) {
                if (field.downgrade) {
                    older[field.property] = field.downgrade.call(object, older[field.property]);
                }
            }
        }
        return older;
    }
}
AutoEncoder.isPatch = false;
//# sourceMappingURL=AutoEncoder.js.map